import { CornerTable } from "../../v2/components/CornerTable.jsx";
import React, { useRef, useState } from "react";
import { Modal } from "../../components/Modal.jsx";
import { T } from "../../app/tokens.js";
import { importDirectory } from "../../data/adminUsersClient.js";

const button = { padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer" };

export function DirectoryImportModal({ onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRequest = useRef(0);

  function edit(value) {
    setCsv(value);
    setPreview(null);
    setResult(null);
    setError("");
  }

  async function chooseFile(event) {
    const request = ++fileRequest.current;
    const file = event.target.files?.[0];
    edit("");
    if (!file) return;
    if (file.size > 256_000) { setError("Choose a CSV file up to 256 KB."); return; }
    setBusy(true);
    try {
      const text = await file.text();
      if (request === fileRequest.current) edit(text);
    } catch { setError("Could not read the CSV file."); }
    finally { setBusy(false); }
  }

  async function submit(mode) {
    setBusy(true);
    setError("");
    try {
      const data = await importDirectory(csv, mode);
      if (mode === "preview") setPreview(data);
      else {
        setResult(data);
        setPreview(null);
        onImported();
      }
    } catch (err) {
      setError(err.message || "Import failed. Refresh the Directory and preview before retrying.");
      setPreview(null);
    } finally { setBusy(false); }
  }

  return <Modal isOpen onClose={() => { if (!busy) onClose(); }} disableBackdropClose={busy}
    title="Import directory (CSV)" ariaLabelledBy="directory-import-title" testId="directory-import-modal" maxWidth={680}>
    <div style={{ padding: 24 }}>
      <p style={{ color: T.mid, marginTop: 0 }}>Import directory metadata only - no accounts or application access are created. Existing disabled status and identity links are preserved.</p>
      <label style={{ display: "block", marginBottom: 12 }}>Choose CSV file
        <input aria-label="Choose CSV file" type="file" accept=".csv,text/csv" disabled={busy || !!result}
          onChange={chooseFile} style={{ display: "block", marginTop: 8 }} autoFocus />
      </label>
      <label style={{ display: "block" }}>Or paste CSV
        <textarea aria-label="Or paste CSV" value={csv} disabled={busy || !!result}
          onChange={event => { fileRequest.current++; edit(event.target.value); }}
          placeholder="name,email,group,org" rows={8}
          style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 8, padding: 10, border: `1px solid ${T.border}`, borderRadius: 6, color: T.dark, background: T.white }} />
      </label>
      <p style={{ color: T.mid, fontSize: T.fsXs }}>Required header: name,email,group,org. Groups: admin, editor, viewer. Organizations: Ripple, JLL.</p>
      {error && <p role="alert" style={{ color: T.red }}>{error}</p>}
      {preview && <div data-testid="directory-import-preview" aria-live="polite">
        <CornerTable style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
          <thead><tr>{["New", "Updated", "Unchanged"].map(label => <th key={label} style={{ padding: 8 }}>{label}</th>)}</tr></thead>
          <tbody><tr>{[preview.adds, preview.updates, preview.unchanged].map((count, index) => <td key={index} style={{ padding: 8 }}>{count}</td>)}</tr></tbody>
        </CornerTable>
        {preview.errors.length > 0 ? <div role="alert" style={{ color: T.red }}>
          <p>Fix all errors before importing. Nothing has been written.</p>
          <ul style={{ maxHeight: 160, overflowY: "auto" }}>{preview.errors.map((item, index) => <li key={index}>Row {item.row}: {item.message}</li>)}</ul>
        </div> : <p style={{ color: T.mid }}>Preview only - nothing has been written. Confirm to import these people.</p>}
      </div>}
      {result && <p role="status" data-testid="directory-import-success">
        Import complete - {result.adds} new, {result.updates} updated, {result.unchanged} unchanged. Directory refreshed.
      </p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <button type="button" style={{ ...button, background: T.white, color: T.dark }} disabled={busy} onClick={onClose}>{result ? "Done" : "Cancel"}</button>
        {!result && <button type="button" style={{ ...button, background: T.white, color: T.dark }} disabled={busy || !csv.trim()} onClick={() => submit("preview")}>{busy ? "Working..." : "Preview"}</button>}
        {preview && !preview.errors.length && <button type="button" style={{ ...button, background: T.blue, color: T.white }} disabled={busy} onClick={() => submit("apply")}>Confirm import</button>}
      </div>
    </div>
  </Modal>;
}
